import { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, ExternalLink, Search } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { Button } from "../../../components/ui/Button/Button";
import Modal from "../../../components/Modal/Modal";
import { useWebsiteReport } from "../useAnalyticsContext";
import type { AnalyticsScope } from "../queryKeys";
import type { ContextResponse, DateRange, TablePayload } from "../types";
import { cellValue, safePageUrl } from "../format";
import { Empty, ErrorState, Loading } from "./States";
import styles from "../Analytics.module.css";
import { pageColumns, tableFilters, type ReportColumn } from "../tableConfig";
export default function ReportTable({
  scope,
  id,
  range,
  context,
  report,
  title,
  columns,
  details = false,
}: {
  scope: AnalyticsScope;
  id: string;
  range: DateRange;
  context: ContextResponse;
  report: string;
  title: string;
  columns: ReportColumn[];
  details?: boolean;
}) {
  const [params, setParams] = useSearchParams(),
    filters = tableFilters(params),
    [search, setSearch] = useState(filters.q);
  const selected = params.get("pageKey");
  const rowFocus = useRef<HTMLButtonElement | null>(null);
  useEffect(() => setSearch(filters.q), [filters.q]);
  const query = useWebsiteReport<TablePayload>(
    scope,
    id,
    range,
    context,
    report,
    filters,
  );
  const detail = useWebsiteReport<TablePayload>(
    scope,
    id,
    range,
    context,
    "pages",
    { page_key: selected ?? undefined, page: 1, page_size: 1 },
    details && !!selected,
  );
  const update = (values: Record<string, string | undefined>) => {
    const next = new URLSearchParams(params);
    Object.entries(values).forEach(([k, v]) =>
      v ? next.set(k, v) : next.delete(k),
    );
    setParams(next);
  };
  const payload = query.data?.payload;
  const close = () => {
    update({ pageKey: undefined });
    requestAnimationFrame(() => rowFocus.current?.focus());
  };
  const row =
    detail.data?.payload.items[0] ??
    payload?.items.find((r) => String(r.page_key) === selected);
  const page = payload?.page ?? filters.page;
  return (
    <section className={styles.panel}>
      <div className={styles.panelHeading}>
        <div>
          <h2>{title}</h2>
          <p>
            {payload
              ? `${payload.total_available_rows.toLocaleString()} available rows`
              : "Explore the available report"}
          </p>
        </div>
        <form
          className={styles.search}
          onSubmit={(e) => {
            e.preventDefault();
            update({ q: search.trim(), page: undefined });
          }}
        >
          <Search size={16} />
          <label className={styles.srOnly} htmlFor={`filter-${report}`}>
            Filter {title.toLowerCase()}
          </label>
          <input
            id={`filter-${report}`}
            placeholder="Filter results…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <Button type="submit" variant="ghost">
            Search
          </Button>
          {filters.q && (
            <Button
              variant="ghost"
              onClick={() => {
                setSearch("");
                update({ q: undefined, page: undefined });
              }}
            >
              Clear
            </Button>
          )}
        </form>
      </div>
      {query.isPending ? (
        <Loading label="Loading report…" />
      ) : query.isError ? (
        <ErrorState error={query.error} retry={() => query.refetch()} />
      ) : !payload?.items.length ? (
        <Empty
          title={filters.q ? "No matching results" : "No rows for this period"}
        >
          {filters.q
            ? "Try another filter or clear your search."
            : "A connected source may not have collected this report yet. Try another period or refresh data."}
        </Empty>
      ) : (
        <>
          <div
            className={styles.tableScroll}
            tabIndex={0}
            role="region"
            aria-label={`${title} table`}
          >
            <table
              className={`${styles.reportTable} ${details ? "" : styles.fullColumns}`}
            >
              <thead>
                <tr>
                  {columns.map((col) => (
                    <th
                      key={col.key}
                      scope="col"
                      className={col.mobile ? undefined : styles.optionalColumn}
                      aria-sort={
                        filters.sort === col.key
                          ? "ascending"
                          : filters.sort === `-${col.key}`
                            ? "descending"
                            : "none"
                      }
                    >
                      <button
                        onClick={() =>
                          update({
                            sort:
                              filters.sort === `-${col.key}`
                                ? col.key
                                : `-${col.key}`,
                            page: undefined,
                          })
                        }
                      >
                        {col.label}
                        <span aria-hidden="true">
                          {" "}
                          {filters.sort === col.key
                            ? "↑"
                            : filters.sort === `-${col.key}`
                              ? "↓"
                              : "↕"}
                        </span>
                      </button>
                    </th>
                  ))}
                  {details && (
                    <th scope="col">
                      <span className={styles.srOnly}>Details</span>
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {payload.items.map((item, index) => (
                  <tr
                    key={String(
                      item.page_key ??
                        item.page ??
                        item.query ??
                        item.country ??
                        item.device ??
                        index,
                    )}
                  >
                    {columns.map((col, i) => (
                      <td
                        key={col.key}
                        className={`${i === 0 ? styles.primaryCell : ""} ${col.mobile ? "" : styles.optionalColumn}`}
                      >
                        {i === 0 ? (
                          <>
                            <span title={String(item[col.key] ?? "")}>
                              {cellValue(item[col.key], col.key)}
                            </span>
                            {details && item.match_status && (
                              <small>
                                {item.match_status === "matched" ||
                                item.match_status === "exact"
                                  ? "Matched sources"
                                  : item.match_status === "gsc_only"
                                    ? "Search Console only"
                                    : item.match_status === "ga4_only"
                                      ? "Google Analytics only"
                                      : "URL match unavailable"}
                              </small>
                            )}
                          </>
                        ) : (
                          cellValue(item[col.key], col.key)
                        )}
                      </td>
                    ))}
                    {details && (
                      <td>
                        <button
                          className={styles.rowButton}
                          onClick={(e) => {
                            rowFocus.current = e.currentTarget;
                            update({ pageKey: String(item.page_key) });
                          }}
                          aria-label={`Details for ${String(item.page)}`}
                        >
                          Details
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className={styles.pagination}>
            <span>
              Page {page} · {payload.total_available_rows.toLocaleString()}{" "}
              available rows
            </span>
            <div>
              <Button
                variant="ghost"
                disabled={page <= 1 || query.isFetching}
                onClick={() => update({ page: String(page - 1) })}
                icon={<ChevronLeft size={16} />}
              >
                Previous
              </Button>
              <Button
                variant="ghost"
                disabled={
                  page * payload.page_size >= payload.total_available_rows ||
                  query.isFetching
                }
                onClick={() => update({ page: String(page + 1) })}
                icon={<ChevronRight size={16} />}
              >
                Next
              </Button>
            </div>
          </div>
        </>
      )}
      {payload?.truncated && (
        <p className={styles.subtleNotice}>
          The source supplied a limited set of rows. This report may not include
          all activity.
        </p>
      )}
      {payload?.limitations?.map((limit, i) => (
        <p className={styles.caption} key={i}>
          {limit}
        </p>
      ))}
      {report === "pages" && (
        <p className={styles.caption}>
          Sessions are associated with a page; adding these rows does not give
          website total sessions. URLs stay separate unless their identity can
          be verified.
        </p>
      )}
      {details && selected && (
        <Modal isOpen onClose={close} title="Page performance" size="large">
          <div className={styles.pageDetails}>
            {detail.isPending ? (
              <Loading label="Loading page details…" />
            ) : detail.isError ? (
              <ErrorState error={detail.error} retry={() => detail.refetch()} />
            ) : row ? (
              <>
                <p className={styles.pageUrl}>{String(row.page)}</p>
                {safePageUrl(String(row.page)) && (
                  <a
                    className={styles.textLink}
                    href={safePageUrl(String(row.page))}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Open original page <ExternalLink size={14} />
                  </a>
                )}
                <dl className={styles.detailsGrid}>
                  {pageColumns
                    .filter((c) => c.key !== "page")
                    .map((c) => (
                      <div key={c.key}>
                        <dt>{c.label}</dt>
                        <dd>{cellValue(row[c.key], c.key)}</dd>
                      </div>
                    ))}
                </dl>
                <p className={styles.caption}>
                  Search and traffic are independent reports. Query-level
                  attribution is not available for this page.
                </p>
              </>
            ) : (
              <Empty title="Page no longer available">
                This page is not in the available report for the selected
                period.
              </Empty>
            )}
          </div>
        </Modal>
      )}
    </section>
  );
}
